import React, { useEffect, useRef, useState } from 'react';
import Snackbar from '@mui/material/Snackbar';
import { useDispatch, useSelector } from 'react-redux';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    MenuItem,
    TextField,
    Typography
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';

import CreditStatement from './creditmgmt/CreditStatement';
import BankTransferReq from './creditmgmt/BankTransferReq';
import {
    addBeneficiaryAPI,
    getKYCAPI,
    getTransferreqInfoAPI,
    initTransferReqAPI,
    updateKYCAPI
} from 'api/CoreMlmmgmtWebServices';
import { setRefreshCreditSummary } from 'store/slice/EcomSlice';

const TRANSFER_SETUP_STEP = {
    LOADING: 'LOADING',
    KYC: 'KYC',
    KYC_SUCCESS: 'KYC_SUCCESS',
    BANK: 'BANK',
    BANK_SUCCESS: 'BANK_SUCCESS',
    BANK_AWAITED: 'BANK_AWAITED',
    AMOUNT: 'AMOUNT',
    STATUS: 'STATUS'
};

const DEFAULT_AADHAR_NUMBER = 'AADHARNO';
const BANK_VERIFICATION_LIMIT_MESSAGE = 'Bank verification attempt limit exceeded. Please connect to Our Customer Support.';
const TRANSFER_AMOUNT_OPTIONS = ['50', '100', '200', '500'];
const isTrueFlag = (value) => value === true || value === 1 || value === '1';

export default function MlmcreditPageImpl() {
    const dispatch = useDispatch();

    const [openBankTransferDialog, setOpenBankTransferDialog] = useState(false);
    const [openPendingTransferDialog, setOpenPendingTransferDialog] = useState(false);
    const [openStatementDialog, setOpenStatementDialog] = useState(false);
    const [openTransferAmountDialog, setOpenTransferAmountDialog] = useState(false);

    const [transferSetupStep, setTransferSetupStep] = useState(TRANSFER_SETUP_STEP.LOADING);
    const [kycInfo, setKycInfo] = useState(null);
    const [panNumber, setPanNumber] = useState('');
    const [aadharNumber, setAadharNumber] = useState(DEFAULT_AADHAR_NUMBER);
    const [accountHolderName, setAccountHolderName] = useState('');
    const [bankIfscCode, setBankIfscCode] = useState('');
    const [bankAccountNumber, setBankAccountNumber] = useState('');
    const [transferAmount, setTransferAmount] = useState('');

    const [isLoading, setIsLoading] = useState(false);
    const [isBalanceLoading, setIsBalanceLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [infoMessage, setInfoMessage] = useState('');
    const [creditBalance, setCreditBalance] = useState(0);
    const [tranferInProgressBalance, setTranferInProgressBalance] = useState(10);

    const [isTransferStatusPolling, setIsTransferStatusPolling] = useState(false);
    const [transferStatusMessage, setTransferStatusMessage] = useState('');
    const [transferStatusCode, setTransferStatusCode] = useState(null);
    const [transferSuccessInfo, setTransferSuccessInfo] = useState(null);

    const transferPollTimeoutRef = useRef(null);
    const isTransferStatusScreen = transferSetupStep === TRANSFER_SETUP_STEP.STATUS;
    const isTransferStatusConcluded =
        isTransferStatusScreen &&
        !isTransferStatusPolling &&
        transferStatusCode !== null;
    const isBankVerificationLocked =
        (kycInfo?.accountattempts ?? 0) >= 5
        || errorMessage.includes(BANK_VERIFICATION_LIMIT_MESSAGE);
    const hasEligibleTransferAmount = TRANSFER_AMOUNT_OPTIONS.some((amountOption) => parseFloat(amountOption) <= creditBalance);

    useEffect(() => {
        return () => {
            if (transferPollTimeoutRef.current) {
                clearTimeout(transferPollTimeoutRef.current);
            }
        };
    }, []);

    const resolveAccountHolderName = (nextKycInfo) => nextKycInfo?.title || '';

    const resolveApiErrorMessage = (response, fallbackMessage) =>
        response?.appError?.errorMsg || response?.panMessage || response?.message || fallbackMessage;

    const resetTransferStatusState = () => {
        if (transferPollTimeoutRef.current) {
            clearTimeout(transferPollTimeoutRef.current);
            transferPollTimeoutRef.current = null;
        }
        setIsTransferStatusPolling(false);
        setTransferStatusMessage('');
        setTransferStatusCode(null);
        setTransferSuccessInfo(null);
    };

    const resetTransferFlowState = () => {
        resetTransferStatusState();
        setTransferSetupStep(TRANSFER_SETUP_STEP.LOADING);
        setKycInfo(null);
        setPanNumber('');
        setAadharNumber(DEFAULT_AADHAR_NUMBER);
        setAccountHolderName('');
        setBankIfscCode('');
        setBankAccountNumber('');
        setTransferAmount('');
        setErrorMessage('');
        setInfoMessage('');
        setIsLoading(false);
    };

    const moveToSetupStepFromKyc = (nextKycInfo) => {
        setKycInfo(nextKycInfo || null);
        setAccountHolderName(resolveAccountHolderName(nextKycInfo));
        setPanNumber(nextKycInfo?.pannumber || '');
        setAadharNumber(nextKycInfo?.aadharnumber || DEFAULT_AADHAR_NUMBER);
        setBankIfscCode(nextKycInfo?.ifsccode || '');
        setBankAccountNumber(nextKycInfo?.payoutaccountno || '');
        setErrorMessage('');
        setInfoMessage('');

        if (!nextKycInfo || !isTrueFlag(nextKycInfo.panverified)) {
            setTransferSetupStep(TRANSFER_SETUP_STEP.KYC);
            return;
        }

        if (isTrueFlag(nextKycInfo.bankaccountverified)) {
            setTransferSetupStep(TRANSFER_SETUP_STEP.AMOUNT);
            return;
        }

        setTransferSetupStep(TRANSFER_SETUP_STEP.KYC_SUCCESS);
    };

    const handleProceedToBankDetails = () => {
        setErrorMessage('');
        setInfoMessage('');
        setTransferSetupStep(TRANSFER_SETUP_STEP.BANK);
    };

    const handleProceedToTransferAmount = () => {
        setErrorMessage('');
        setInfoMessage('');
        setTransferSetupStep(TRANSFER_SETUP_STEP.AMOUNT);
    };

    const fetchKycAndPrepareTransferFlow = () => {
        setIsLoading(true);
        setTransferSetupStep(TRANSFER_SETUP_STEP.LOADING);
        setErrorMessage('');
        setInfoMessage('');

        getKYCAPI(
            {},
            (apiResponse) => {
                setIsLoading(false);
                if (apiResponse?.success === false) {
                    setErrorMessage(resolveApiErrorMessage(apiResponse, 'Unable to fetch KYC details.'));
                    setTransferSetupStep(TRANSFER_SETUP_STEP.KYC);
                    return;
                }
                moveToSetupStepFromKyc(apiResponse?.KycInfo || apiResponse?.kycInfo || null);
            },
            (error) => {
                setIsLoading(false);
                setErrorMessage(error?.message || 'Unable to fetch KYC details.');
                setTransferSetupStep(TRANSFER_SETUP_STEP.KYC);
            }
        );
    };

    const handleRaiseBankTransferRequest = () => {
        resetTransferFlowState();
        setOpenTransferAmountDialog(true);
        fetchKycAndPrepareTransferFlow();
    };

    const handleCloseTransferAmountDialog = () => {
        setOpenTransferAmountDialog(false);
        resetTransferFlowState();
    };

    const handleCloseTransferStatusDialog = () => {
        dispatch(setRefreshCreditSummary());
        handleCloseTransferAmountDialog();
    };

    const handleSubmitKyc = () => {
        const trimmedPanNumber = panNumber.trim().toUpperCase();
        const trimmedAadharNumber = DEFAULT_AADHAR_NUMBER;
        const trimmedAccountHolderName = accountHolderName.trim();

        if (!trimmedPanNumber) {
            setErrorMessage('PAN Number is mandatory to raise bank transfer request.');
            return;
        }

        if (!trimmedAccountHolderName) {
            setErrorMessage('Account holder name is not available for KYC verification.');
            return;
        }

        setIsLoading(true);
        setErrorMessage('');
        setInfoMessage('');

        updateKYCAPI(
            {
                panNumber: trimmedPanNumber,
                aadharNumber: trimmedAadharNumber,
                name: trimmedAccountHolderName
            },
            (apiResponse) => {
                setIsLoading(false);
                const updatedKycInfo = apiResponse?.KycInfo || apiResponse?.kycInfo || null;

                if (!isTrueFlag(updatedKycInfo?.panverified)) {
                    setErrorMessage(resolveApiErrorMessage(apiResponse, 'PAN verification failed.'));
                    return;
                }

                moveToSetupStepFromKyc(updatedKycInfo);
            },
            (error) => {
                setIsLoading(false);
                setErrorMessage(error?.message || 'Unable to verify KYC details.');
            }
        );
    };

    const handleSubmitBeneficiary = () => {
        const trimmedAccountHolderName = accountHolderName.trim();
        const trimmedIfscCode = bankIfscCode.trim().toUpperCase();
        const trimmedAccountNumber = bankAccountNumber.trim();

        if (!trimmedAccountHolderName) {
            setErrorMessage('Account holder name is required to add beneficiary.');
            return;
        }

        if (!trimmedIfscCode) {
            setErrorMessage('Bank IFSC code is mandatory.');
            return;
        }

        if (!trimmedAccountNumber) {
            setErrorMessage('Bank account number is mandatory.');
            return;
        }

        setIsLoading(true);
        setErrorMessage('');
        setInfoMessage('');

        addBeneficiaryAPI(
            {
                name: trimmedAccountHolderName,
                ifscCode: trimmedIfscCode,
                accountNumber: trimmedAccountNumber
            },
            (apiResponse) => {
                setIsLoading(false);
                if (apiResponse?.success === false) {
                    const failureMessage = resolveApiErrorMessage(apiResponse, 'Unable to add beneficiary.');
                    if (failureMessage.includes(BANK_VERIFICATION_LIMIT_MESSAGE)) {
                        setKycInfo((prev) => ({ ...(prev || {}), accountattempts: 5 }));
                    }
                    setErrorMessage(failureMessage);
                    return;
                }

                const updatedKycInfo = apiResponse?.KycInfo || apiResponse?.kycInfo || null;
                if (isTrueFlag(updatedKycInfo?.bankaccountverified)) {
                    setKycInfo(updatedKycInfo);
                    setTransferSetupStep(TRANSFER_SETUP_STEP.BANK_SUCCESS);
                    return;
                }

                if (updatedKycInfo) {
                    setKycInfo(updatedKycInfo);
                }
                setTransferSetupStep(TRANSFER_SETUP_STEP.BANK_AWAITED);
                setInfoMessage('The account verification is submitted but confirmation is awaited. We will get back to you, once we get confirmation.');
            },
            (error) => {
                setIsLoading(false);
                setErrorMessage(error?.message || 'Unable to add beneficiary.');
            }
        );
    };

    const startTransferStatusPolling = (transferreqId, amount, attemptNumber) => {
        if (transferPollTimeoutRef.current) {
            clearTimeout(transferPollTimeoutRef.current);
        }

        if (attemptNumber > 5) {
            setIsTransferStatusPolling(false);
            setTransferStatusCode(1);
            setTransferStatusMessage('Bank Transfer Request was initiated but status is still pending. We will inform you once the transfer is processed.');
            return;
        }

        getTransferreqInfoAPI(
            transferreqId,
            (response) => {
                const reqstatus = response?.reqstatus;

                if (reqstatus === 2) {
                    const requestedAmount = parseFloat(response?.amount ?? amount ?? 0);
                    const transferredAmount = parseFloat(response?.transferedamount ?? amount ?? 0);
                    const taxDeducted = parseFloat(response?.taxdeducted ?? 0);
                    setIsTransferStatusPolling(false);
                    setTransferStatusCode(2);
                    setTransferSuccessInfo({
                        requestedAmount,
                        transferredAmount,
                        taxDeducted
                    });
                    setTransferStatusMessage(
                        `Transfer request of ₹${requestedAmount.toFixed(2)}/- was accepted and ₹${transferredAmount.toFixed(2)}/- is transferred after deducting TDS ₹${taxDeducted.toFixed(2)}/-.`
                    );
                    return;
                }

                if (reqstatus !== 1) {
                    setIsTransferStatusPolling(false);
                    setTransferStatusCode(reqstatus ?? 0);
                    if (reqstatus === 3) {
                        setTransferStatusMessage('Bank transfer initiation failed. We will analyse and get back to you.');
                    } else {
                        setTransferStatusMessage(`Bank transfer for ₹${amount.toFixed(2)}/- could not be completed.`);
                    }
                    return;
                }

                setTransferStatusCode(1);
                setTransferStatusMessage(`Bank transfer for ₹${amount.toFixed(2)}/- is initiated and waiting for transfer status.`);
                transferPollTimeoutRef.current = setTimeout(() => {
                    startTransferStatusPolling(transferreqId, amount, attemptNumber + 1);
                }, 5000);
            },
            () => {
                transferPollTimeoutRef.current = setTimeout(() => {
                    startTransferStatusPolling(transferreqId, amount, attemptNumber + 1);
                }, 5000);
            }
        );
    };

    const handleInitiateTransfer = () => {
        const amount = parseFloat(transferAmount);

        if (!transferAmount || amount <= 0) {
            setErrorMessage('Please enter a valid amount');
            return;
        }

        if (amount > creditBalance) {
            setErrorMessage(`Amount cannot exceed your available balance of ₹${creditBalance.toFixed(2)}`);
            return;
        }

        setIsLoading(true);
        setErrorMessage('');
        setInfoMessage('');

        initTransferReqAPI(
            { amount },
            (apiResponse) => {
                const transferreqInfo = apiResponse?.transferreqInfo || apiResponse?.TransferreqInfo;
                const transferreqId = transferreqInfo?.id;

                setIsLoading(false);
                setTransferAmount('');

                if (!transferreqId) {
                    setErrorMessage('Transfer request created but tracking id is missing.');
                    return;
                }

                setTransferSetupStep(TRANSFER_SETUP_STEP.STATUS);
                setTransferStatusCode(1);
                setTransferStatusMessage(`Bank transfer for ₹${amount.toFixed(2)}/- is initiated and waiting for transfer status.`);
                setIsTransferStatusPolling(true);
                startTransferStatusPolling(transferreqId, amount, 1);
            },
            (error) => {
                setIsLoading(false);
                setErrorMessage(
                    typeof error === 'string'
                        ? error
                        : error?.message || 'An error occurred while initiating transfer request. Please try again.'
                );
            }
        );
    };

    const handleCloseBankTransferDialog = () => {
        setOpenBankTransferDialog(false);
    };

    const handleOpenPendingTransferDialog = () => {
        setOpenPendingTransferDialog(true);
    };

    const handleClosePendingTransferDialog = () => {
        setOpenPendingTransferDialog(false);
    };

    const handleOpenStatementDialog = () => {
        setOpenStatementDialog(true);
    };

    const handleCloseStatementDialog = () => {
        setOpenStatementDialog(false);
    };

    const isAmountValid = () => {
        const amount = parseFloat(transferAmount);
        return transferAmount && amount > 0 && amount <= creditBalance;
    };

    const getTransferDialogTitle = () => {
        if (isTransferStatusScreen) {
            return 'Bank Transfer Request';
        }
        if (transferSetupStep === TRANSFER_SETUP_STEP.KYC) {
            return 'KYC Verification';
        }
        if (transferSetupStep === TRANSFER_SETUP_STEP.KYC_SUCCESS) {
            return 'KYC Verification';
        }
        if (transferSetupStep === TRANSFER_SETUP_STEP.BANK) {
            return 'Bank Account Details';
        }
        if (transferSetupStep === TRANSFER_SETUP_STEP.BANK_SUCCESS) {
            return 'Account Verification';
        }
        if (transferSetupStep === TRANSFER_SETUP_STEP.BANK_AWAITED) {
            return 'Account Verification';
        }
        if (transferSetupStep === TRANSFER_SETUP_STEP.LOADING) {
            return 'Preparing Transfer';
        }
        return 'Bank Transfer Request';
    };

    const managerBankTransferRequest = () => {
        handleRaiseBankTransferRequest();
    };

    return (
        <Grid container spacing={3} sx={{}}>
            <Grid item xs={12}>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12}>
                        <Card sx={{ backgroundColor: 'success.50', height: '100%' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <AccountBalanceIcon sx={{ fontSize: 40, color: 'success.main' }} />
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.875rem', display: 'block' }}>
                                                Credit Balance
                                            </Typography>
                                            {isBalanceLoading ? (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.5 }}>
                                                    <CircularProgress size={24} color="success" />
                                                </Box>
                                            ) : (
                                                <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                                                    ₹{creditBalance.toFixed(2)}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>

                                {tranferInProgressBalance > 0 && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 1,
                                            mb: 2,
                                            p: 1.5,
                                            backgroundColor: 'secondary.100',
                                            borderRadius: 1
                                        }}
                                    >
                                        <InfoIcon sx={{ fontSize: 20, color: 'info.main' }} />
                                        <Typography variant="body2" sx={{ fontSize: '0.80rem', color: 'info.main', fontWeight: 500 }}>
                                            In Progress - Transfer request of worth ₹{tranferInProgressBalance.toFixed(2)}
                                        </Typography>
                                    </Box>
                                )}

                                <Divider sx={{ mt: tranferInProgressBalance > 0 ? 2 : 5, mb: 2 }} />

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Button
                                        variant="text"
                                        color="primary"
                                        startIcon={<VisibilityIcon />}
                                        onClick={handleOpenPendingTransferDialog}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            '&:hover': {
                                                backgroundColor: 'primary.50'
                                            }
                                        }}
                                    >
                                        Transfer History
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        startIcon={<SendIcon />}
                                        onClick={managerBankTransferRequest}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            '&:hover': {
                                                backgroundColor: 'success.100'
                                            }
                                        }}
                                        disabled={creditBalance === 0}
                                    >
                                        Bank Transfer
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Box sx={{ mb: 1, p: 1 }}>
                    <CreditStatement
                        setBalance={setCreditBalance}
                        setTranferInProgressBalance={setTranferInProgressBalance}
                        setBalanceLoading={setIsBalanceLoading}
                    />
                </Box>
            </Grid>

            <Dialog
                open={openTransferAmountDialog}
                onClose={isTransferStatusScreen && !isTransferStatusConcluded ? undefined : handleCloseTransferAmountDialog}
                maxWidth="sm"
                fullWidth
                disableEscapeKeyDown={isTransferStatusScreen && !isTransferStatusConcluded}
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {getTransferDialogTitle()}
                        </Typography>
                        {!(isTransferStatusScreen && !isTransferStatusConcluded) ? (
                            <IconButton
                                onClick={handleCloseTransferAmountDialog}
                                sx={{
                                    color: 'text.secondary',
                                    '&:hover': {
                                        backgroundColor: 'action.hover'
                                    }
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        ) : null}
                    </Box>
                </DialogTitle>
                <DialogContent dividers sx={{ backgroundColor: '#f5f5f5', position: 'relative', p: 2 }}>
                    {transferSetupStep === TRANSFER_SETUP_STEP.LOADING ? (
                        <Box
                            sx={{
                                minHeight: 240,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 2
                            }}
                        >
                            <CircularProgress />
                            <Typography variant="body2" color="text.secondary">
                                Fetching KYC details...
                            </Typography>
                        </Box>
                    ) : transferSetupStep === TRANSFER_SETUP_STEP.KYC ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ p: 2, backgroundColor: '#FFFFFF', borderRadius: 1, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                    KYC verification is required before you can initiate a bank transfer.
                                </Typography>
                            </Box>
                            <Box sx={{ p: 2, backgroundColor: '#FFFFFF', borderRadius: 1, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                <TextField
                                    fullWidth
                                    label="Name as per PAN"
                                    value={accountHolderName}
                                    onChange={(e) => {
                                        setAccountHolderName(e.target.value);
                                        setErrorMessage('');
                                        setInfoMessage('');
                                    }}
                                    sx={{ mb: 2 }}
                                />
                                <TextField
                                    fullWidth
                                    label="PAN Number"
                                    value={panNumber}
                                    onChange={(e) => {
                                        setPanNumber(e.target.value.toUpperCase());
                                        setErrorMessage('');
                                        setInfoMessage('');
                                    }}
                                    sx={{ mb: 0 }}
                                />
                            </Box>
                        </Box>
                    ) : transferSetupStep === TRANSFER_SETUP_STEP.KYC_SUCCESS ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                py: 4,
                                gap: 2
                            }}
                        >
                            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
                                PAN Verified Successfully
                            </Typography>
                            <Typography variant="body1" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                                Your PAN verification is complete. Continue to add your bank account details.
                            </Typography>
                        </Box>
                    ) : transferSetupStep === TRANSFER_SETUP_STEP.BANK ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ p: 2, backgroundColor: '#FFFFFF', borderRadius: 1, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Account Holder Name
                                </Typography>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {accountHolderName}
                                </Typography>
                            </Box>
                            <Box sx={{ p: 2, backgroundColor: '#FFFFFF', borderRadius: 1, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                <TextField
                                    fullWidth
                                    label="Bank IFSC Code"
                                    value={bankIfscCode}
                                    onChange={(e) => {
                                        setBankIfscCode(e.target.value.toUpperCase());
                                        setErrorMessage('');
                                        setInfoMessage('');
                                    }}
                                    sx={{ mb: 2 }}
                                />
                                <TextField
                                    fullWidth
                                    label="Bank Account Number"
                                    value={bankAccountNumber}
                                    onChange={(e) => {
                                        setBankAccountNumber(e.target.value);
                                        setErrorMessage('');
                                        setInfoMessage('');
                                    }}
                                />
                            </Box>
                        </Box>
                    ) : transferSetupStep === TRANSFER_SETUP_STEP.BANK_SUCCESS ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                py: 4,
                                gap: 2
                            }}
                        >
                            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
                                Account Verified Successfully
                            </Typography>
                            <Typography variant="body1" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                                Your bank account verification is complete. Continue to initiate the bank transfer request.
                            </Typography>
                        </Box>
                    ) : transferSetupStep === TRANSFER_SETUP_STEP.BANK_AWAITED ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                py: 4,
                                gap: 2
                            }}
                        >
                            <InfoIcon sx={{ fontSize: 64, color: 'warning.main' }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'warning.main' }}>
                                Account Verification Submitted
                            </Typography>
                            <Typography variant="body1" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                                The account verification is submitted but confirmation is awaited. We will get back to you, once we get confirmation.
                            </Typography>
                        </Box>
                    ) : isTransferStatusScreen ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                py: 4,
                                gap: 2
                            }}
                        >
                            {isTransferStatusPolling ? (
                                <CircularProgress />
                            ) : transferStatusCode === 2 ? (
                                <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />
                            ) : (
                                <InfoIcon sx={{ fontSize: 64, color: 'info.main' }} />
                            )}
                            <Typography variant="h6" sx={{ fontWeight: 600, color: transferStatusCode === 2 ? 'success.main' : 'info.main' }}>
                                {transferStatusCode === 2 ? 'Success!' : transferStatusCode === 3 ? 'Transfer Failed' : 'Transfer Initiated'}
                            </Typography>
                            {transferStatusCode === 2 && transferSuccessInfo ? (
                                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            p: 2,
                                            backgroundColor: '#FFFFFF',
                                            borderRadius: 1,
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                            textAlign: 'center'
                                        }}
                                    >
                                        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                            Transfer request of{' '}
                                            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                                ₹{transferSuccessInfo.requestedAmount.toFixed(2)}/-
                                            </Box>{' '}
                                            was accepted.
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            p: 2,
                                            backgroundColor: '#FFFFFF',
                                            borderRadius: 1,
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                            textAlign: 'center'
                                        }}
                                    >
                                        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                                ₹{transferSuccessInfo.transferredAmount.toFixed(2)}/-
                                            </Box>{' '}
                                            is transferred after deducting TDS{' '}
                                            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                                ₹{transferSuccessInfo.taxDeducted.toFixed(2)}/-
                                            </Box>
                                            .
                                        </Typography>
                                    </Box>
                                </Box>
                            ) : (
                                <Typography variant="body1" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                                    {transferStatusMessage}
                                </Typography>
                            )}
                        </Box>
                    ) : (
                        <>
                            <Box
                                sx={{
                                    mb: 2,
                                    p: 2,
                                    backgroundColor: '#FFFFFF',
                                    borderRadius: 1,
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        mb: tranferInProgressBalance > 0 ? 2 : 0
                                    }}
                                >
                                    <Typography variant="body2" color="text.secondary">
                                        Available Balance:
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                                        ₹{creditBalance.toFixed(2)}
                                    </Typography>
                                </Box>

                                {tranferInProgressBalance > 0 && (
                                    <>
                                        <Divider sx={{ my: 1.5 }} />
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between'
                                            }}
                                        >
                                            <Typography variant="body2" color="text.secondary">
                                                Transfer in Progress:
                                            </Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'info.main' }}>
                                                ₹{tranferInProgressBalance.toFixed(2)}
                                            </Typography>
                                        </Box>
                                    </>
                                )}
                            </Box>

                            <Box
                                sx={{
                                    p: 2,
                                    backgroundColor: '#FFFFFF',
                                    borderRadius: 1,
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                                }}
                            >
                                {!hasEligibleTransferAmount && (
                                    <Alert severity="warning" sx={{ mb: 2 }}>
                                        Minimum balance should be 50/- to initiate transfer.
                                    </Alert>
                                )}
                                <TextField
                                    select
                                    fullWidth
                                    label="Select Amount"
                                    value={transferAmount}
                                    onChange={(e) => {
                                        setTransferAmount(e.target.value);
                                        setErrorMessage('');
                                    }}
                                    error={!!errorMessage}
                                    disabled={!hasEligibleTransferAmount}
                                >
                                    {TRANSFER_AMOUNT_OPTIONS.map((amountOption) => (
                                        <MenuItem
                                            key={amountOption}
                                            value={amountOption}
                                            disabled={parseFloat(amountOption) > creditBalance}
                                        >
                                            ₹{amountOption}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>
                        </>
                    )}

                    {errorMessage && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {errorMessage}
                        </Alert>
                    )}
                    {infoMessage && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            {infoMessage}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    {transferSetupStep === TRANSFER_SETUP_STEP.LOADING ? (
                        <Button onClick={handleCloseTransferAmountDialog} color="inherit" sx={{ textTransform: 'none' }}>
                            Cancel
                        </Button>
                    ) : transferSetupStep === TRANSFER_SETUP_STEP.KYC ? (
                        <>
                            <Button onClick={handleCloseTransferAmountDialog} color="inherit" sx={{ textTransform: 'none' }}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSubmitKyc}
                                variant="contained"
                                color="primary"
                                sx={{ textTransform: 'none' }}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Verifying...' : 'Verify KYC'}
                            </Button>
                        </>
                    ) : transferSetupStep === TRANSFER_SETUP_STEP.KYC_SUCCESS ? (
                        <>
                            <Button onClick={handleCloseTransferAmountDialog} color="inherit" sx={{ textTransform: 'none' }}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleProceedToBankDetails}
                                variant="contained"
                                color="primary"
                                sx={{ textTransform: 'none' }}
                            >
                                Next
                            </Button>
                        </>
                    ) : transferSetupStep === TRANSFER_SETUP_STEP.BANK ? (
                        <>
                            <Button onClick={handleCloseTransferAmountDialog} color="inherit" sx={{ textTransform: 'none' }}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSubmitBeneficiary}
                                variant="contained"
                                color="primary"
                                sx={{ textTransform: 'none' }}
                                disabled={isLoading || isBankVerificationLocked}
                            >
                                {isLoading ? 'Verifying...' : 'Verify Bank'}
                            </Button>
                        </>
                    ) : transferSetupStep === TRANSFER_SETUP_STEP.BANK_SUCCESS ? (
                        <>
                            <Button onClick={handleCloseTransferAmountDialog} color="inherit" sx={{ textTransform: 'none' }}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleProceedToTransferAmount}
                                variant="contained"
                                color="primary"
                                sx={{ textTransform: 'none' }}
                            >
                                Next
                            </Button>
                        </>
                    ) : transferSetupStep === TRANSFER_SETUP_STEP.BANK_AWAITED ? (
                        <Button
                            onClick={handleCloseTransferAmountDialog}
                            variant="contained"
                            color="primary"
                            sx={{ textTransform: 'none' }}
                        >
                            Close
                        </Button>
                    ) : isTransferStatusScreen ? (
                        isTransferStatusConcluded ? (
                            <Button
                                onClick={handleCloseTransferStatusDialog}
                                variant="contained"
                                color="primary"
                                sx={{ textTransform: 'none' }}
                            >
                                OK
                            </Button>
                        ) : null
                    ) : (
                        <>
                            <Button onClick={handleCloseTransferAmountDialog} color="inherit" sx={{ textTransform: 'none' }}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleInitiateTransfer}
                                variant="contained"
                                color="primary"
                                disabled={isLoading || !isAmountValid()}
                                sx={{ textTransform: 'none' }}
                            >
                                {isLoading ? 'Processing...' : 'Initiate'}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog
                open={openStatementDialog}
                onClose={handleCloseStatementDialog}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        minHeight: '80vh',
                        maxHeight: '90vh'
                    }
                }}
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Credit Statement
                        </Typography>
                        <IconButton
                            onClick={handleCloseStatementDialog}
                            sx={{
                                color: 'text.secondary',
                                '&:hover': {
                                    backgroundColor: 'action.hover'
                                }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    <CreditStatement />
                </DialogContent>
            </Dialog>

            <Dialog
                open={openBankTransferDialog}
                onClose={handleCloseBankTransferDialog}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        minHeight: '80vh',
                        maxHeight: '90vh'
                    }
                }}
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Bank Transfer History
                        </Typography>
                        <IconButton
                            onClick={handleCloseBankTransferDialog}
                            sx={{
                                color: 'text.secondary',
                                '&:hover': {
                                    backgroundColor: 'action.hover'
                                }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    <BankTransferReq />
                </DialogContent>
            </Dialog>

            <Dialog
                open={openPendingTransferDialog}
                onClose={handleClosePendingTransferDialog}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        minHeight: '80vh',
                        maxHeight: '90vh'
                    }
                }}
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Transfer History
                        </Typography>
                        <IconButton
                            onClick={handleClosePendingTransferDialog}
                            sx={{
                                color: 'text.secondary',
                                '&:hover': {
                                    backgroundColor: 'action.hover'
                                }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    <BankTransferReq />
                </DialogContent>
            </Dialog>

        </Grid>
    );
}
